import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Badge, Col, Container, Row } from 'reactstrap';
import moment from 'moment';

import Layout from '@/components/Layout/Layout';
import MainHeading from '@/components/common/MainHeading';
import CardLayout from '@/components/Layout/CardLayout';
import FullPageLoader from '@/components/loader/FullPageLoader';
import { FormService } from '@/services/FormService';
import {
  IFormColumn,
  IFormData,
  IFormSection,
  IFormSubmissionPayload,
  IFormSubmissionValue,
  FormType,
} from '@/interfaces/form.model';
import { showToastNotification } from '@/utility/helper';
import DynamicFormInput from '../../../../../components/Form/DynamicFormInput';
import { IUser } from '@/interfaces/user.model';
import SearchSelectBox from '@/components/ui/SearchSelectBox';
import Label from '@/components/ui/Label';

type FormValue = IFormSubmissionValue;
type FormSubmissionData = IFormSubmissionPayload['data'];
type FormValueState = Record<string, FormValue>;
type FormErrorState = Record<string, string>;
type FormResponseData = IFormData | { officeForm?: IFormData[] };

const getEmptySection = (
  name: string,
  sequence: number,
  columns: IFormColumn[],
): IFormSection => ({
  id: `section-${sequence}`,
  name,
  sequence,
  columns,
});

const FormView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  console.log(location?.state?.isSubmitted, 'location');

  const queryParams = new URLSearchParams(location.search);
  const userId = queryParams.get('userId');
  const [formData, setFormData] = useState<IFormData | null>(null);
  const [employeeList, setEmployeeList] = useState<IUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formValues, setFormValues] = useState<FormValueState>({});
  const [errors, setErrors] = useState<FormErrorState>({});
  const [managerId, setManagerId] = useState<string>('');
  const [managerRemark, setManagerRemark] = useState<string>('');
  const [managerRating, setManagerRating] = useState<number>(0);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const formType = location?.state?.formType;
  const isMyForm = formType === FormType.MyForms;
  const isTeamForm = formType === FormType.TeamForms;
  const isTeamSubmission = formType === FormType.TeamSubmission;

  const getColumnIdentifier = useCallback((column: IFormColumn) => {
    if (column.id) return column.id;
    // const normalizedColumnName = column.columnName?.trim();
    const fallbackLabel = column.label
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    return column.id || fallbackLabel || '';
  }, []);

  const getColumnPayloadId = useCallback(
    (column: IFormColumn) => {
      return column.id || getColumnIdentifier(column);
    },
    [getColumnIdentifier],
  );

  const getFormDetails = useCallback(async () => {
    if (!id) return;
    // if (!userId) return;

    try {
      setIsLoading(true);
      const response = await FormService.getFormById(id, userId || '');
      console.log(response, 'response');

      if (response?.status) {
        const responseData = response.data as FormResponseData;
        let fetchedData: IFormData | undefined;

        if ('officeForm' in responseData) {
          fetchedData = responseData.officeForm?.[0];
        } else {
          fetchedData = responseData as IFormData;
        }

        if (fetchedData) {
          setFormData(fetchedData);

          // Pre-fill form values from fetched columns
          const initialValues: FormValueState = {};
          const allColumns =
            fetchedData.sections?.flatMap((section) => section.columns) ||
            fetchedData.columns ||
            [];

          allColumns.forEach((column) => {
            const identifier = getColumnIdentifier(column);
            if (column.value !== undefined && column.value !== null) {
              initialValues[identifier] = column.value;
            }
          });
          setFormValues(initialValues);

          if (fetchedData.managerRemark) {
            setManagerRemark(fetchedData.managerRemark);
          }
          if (fetchedData.managerRating !== undefined) {
            setManagerRating(fetchedData.managerRating);
          }
          if (fetchedData?.managerId) {
            setManagerId(fetchedData.managerId);
          }
        } else {
          showToastNotification('danger', 'Form details not found');
        }
      } else {
        showToastNotification(
          'danger',
          response?.message || 'Failed to fetch form details',
        );
      }
    } catch (error: unknown) {
      showToastNotification(
        'danger',
        error instanceof Error ? error.message : 'Error loading form',
      );
    } finally {
      setIsLoading(false);
    }
  }, [id, userId, getColumnIdentifier]);

  useEffect(() => {
    getFormDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, userId]);

  const employeeOptions = useMemo(() => {
    return employeeList.map((employee) => ({
      value: employee.id,
      label: employee.name,
    }));
  }, [employeeList]);

  const groupedSections = useMemo(() => {
    if (formData?.sections?.length) {
      return [...formData.sections].sort(
        (left, right) => (left.sequence || 0) - (right.sequence || 0),
      );
    }

    if (formData?.columns?.length) {
      const groupedMap = formData.columns.reduce<Record<string, IFormColumn[]>>(
        (accumulator, column) => {
          const sectionName = column.section?.trim() || 'Default Section';
          if (!accumulator[sectionName]) {
            accumulator[sectionName] = [];
          }
          accumulator[sectionName].push(column);
          return accumulator;
        },
        {},
      );

      return Object.entries(groupedMap).map(([sectionName, columns], index) =>
        getEmptySection(
          sectionName,
          index + 1,
          [...columns].sort(
            (left, right) => (left.sequence || 0) - (right.sequence || 0),
          ),
        ),
      );
    }

    return [];
  }, [formData]);

  const handleInputChange = (identifier: string, value: FormValue) => {
    // console.log(identifier, "identifier");

    setFormValues((previous) => ({
      ...previous,
      [identifier]: value,
    }));

    if (errors[identifier]) {
      setErrors((previous) => {
        const nextErrors = { ...previous };
        delete nextErrors[identifier];
        return nextErrors;
      });
    }
  };

  const validateForm = (sections: IFormSection[]) => {
    const nextErrors: FormErrorState = {};

    sections.forEach((section) => {
      section.columns.forEach((column) => {
        if (!column.required) return;

        const identifier = getColumnIdentifier(column);
        const value = formValues[identifier];
        const isEmpty =
          value === undefined ||
          value === null ||
          (typeof value === 'string' && value.trim() === '') ||
          (Array.isArray(value) && value.length === 0);

        if (isEmpty) {
          nextErrors[identifier] = `${column.label} is required`;
        }
      });
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayloadData = (sections: IFormSection[]) => {
    return sections.reduce<FormSubmissionData>((accumulator, section) => {
      section.columns.forEach((column) => {
        const identifier = getColumnIdentifier(column);
        accumulator.push({
          id: getColumnPayloadId(column),
          value: formValues[identifier] ?? null,
        });
      });

      return accumulator;
    }, []);
  };

  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (!validateForm(groupedSections)) {
      showToastNotification('danger', 'Please fill all required fields');
      return;
    }

    const payload: IFormSubmissionPayload = {
      formId: formData?.id || id || '',
      status: 'submitted',
      managerId: managerId || undefined,
      data: buildPayloadData(groupedSections),
    };

    try {
      const response = await FormService.formSubmission(payload);
      if (response?.status) {
        getFormDetails();
        showToastNotification('success', response.message);
      } else {
        showToastNotification(
          'danger',
          response?.message || 'Failed to submit form',
        );
      }
    } catch (error: unknown) {
      showToastNotification(
        'danger',
        error instanceof Error ? error.message : 'Error submitting form',
      );
    }
  };

  const getEmployeeList = useCallback(async () => {
    try {
      const response = await FormService.getEmployee();
      const newData = response.data.user.filter(
        (employee: IUser) =>
          !(
            employee.department?.name === 'Intern' ||
            employee.status === 'exEmployee'
          ),
      );

      if (response?.status) {
        setEmployeeList(newData);
      }
    } catch (error: unknown) {
      showToastNotification(
        'danger',
        error instanceof Error ? error.message : 'Error loading employee list',
      );
    }
  }, []);

  useEffect(() => {
    getEmployeeList();
  }, [getEmployeeList]);

  const submitManagerReview = async () => {
    if (!location?.state?.id) {
      return;
    }
    if (!managerRemark.trim() && managerRating === 0) {
      showToastNotification('danger', 'Please provide a remark or rating');
      return;
    }

    try {
      setIsSubmittingReview(true);
      const payload = {
        managerRemark: managerRemark,
        managerRating: managerRating,
      };
      const response = await FormService.submitManagerReview(
        location?.state?.id || '',
        payload,
      );
      if (response?.status) {
        showToastNotification('success', response.message);
        getFormDetails();
      } else {
        showToastNotification(
          'danger',
          response?.message || 'Failed to submit review and rating',
        );
      }
    } catch (error: unknown) {
      showToastNotification(
        'danger',
        error instanceof Error
          ? error.message
          : 'Error submitting review and rating',
      );
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const StarRating = ({
    rating,
    interactive = false,
  }: {
    rating: number;
    interactive?: boolean;
  }) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      let iconClass = 'bi-star';
      if (rating >= i) {
        iconClass = 'bi-star-fill';
      } else if (rating > i - 1) {
        iconClass = 'bi-star-half';
      }

      stars.push(
        <i
          key={i}
          className={`bi ${iconClass} me-1 fs-1`}
          style={{
            color: rating >= i - 0.5 ? '#ffc107' : '#e4e5e9',
            cursor: interactive ? 'pointer' : 'default',
          }}
          onClick={
            interactive
              ? () => setManagerRating(i === rating ? 0 : i)
              : undefined
          }
          onMouseMove={
            interactive
              ? (e) => {
                const rect = (
                  e.currentTarget as HTMLElement
                ).getBoundingClientRect();
                const x = e.clientX - rect.left;
                if (x < rect.width / 2) {
                  // Logic for half stars if needed, but for simplicity let's stick to full or half on click
                }
              }
              : undefined
          }
        />,
      );
    }
    return <div className="d-flex align-items-center mb-2">{stars}</div>;
  };

  if (isLoading) {
    return (
      <Layout>
        <FullPageLoader />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="form-view-container pb-5">
        <MainHeading title={'Form Preview'} showbBackButton />

        <Container className="mt-4 px-0" style={{ maxWidth: '900px' }}>
          <CardLayout cardStyle="mb-4 border-0 shadow-sm p-0 bg-white rounded-4 overflow-hidden position-relative">
            <Badge
              color="light"
              className="text-primary border text-uppercase mb-3 px-3 py-2 fw-semibold letter-spacing-1"
            >
              {'Self-assessment'}
            </Badge>
            <div>
              <h2 className="fw-bold mb-2 text-dark">
                {formData?.name || 'Untitled Form'}
              </h2>
              {formData?.description && (
                <p className="text-secondary fs-5 lh-base mb-0">
                  {formData?.description}
                </p>
              )}
            </div>

            <hr />
            <Row className="text-muted small fw-medium">
              <Col xs="auto" className="d-flex align-items-center me-4">
                <i className="bi bi-calendar3 me-2" />
                {formData?.createdAt
                  ? moment(formData.createdAt).format('MMMM YYYY')
                  : 'Period: N/A'}
              </Col>
              <Col xs="auto" className="d-flex align-items-center me-4">
                <i className="bi bi-clock me-2" />
                Est. 15-20 minutes
              </Col>
              <Col xs="auto" className="d-flex align-items-center">
                <i className="bi bi-shield-lock me-2" />
                Confidential
              </Col>
            </Row>
          </CardLayout>

          {groupedSections?.length > 0 ? (
            groupedSections?.map((section) => (
              <CardLayout
                key={section?.id}
                cardStyle="mb-4 border-0 shadow-sm p-0 bg-white rounded-4"
              >
                <div className="d-flex align-items-center mb-4 pb-2 border-bottom">
                  <div
                    className="section-icon-wrapper bg-primary-subtle p-2 rounded-3 me-3 d-flex align-items-center justify-content-center"
                    style={{ width: '36px', height: '36px' }}
                  >
                    <i className="bi bi-file-earmark-text text-primary fs-3" />
                  </div>
                  <h4 className="fw-bold mb-0 text-dark">{section?.name}</h4>
                </div>

                <Row className="review-form-labels">
                  {section?.columns?.map((column) => {
                    // const identifier = getColumnIdentifier(column);
                    return (
                      <Col
                        key={column?.id}
                        lg={section?.columns?.length > 2 ? 12 : 12}
                      >
                        <DynamicFormInput
                          column={column}
                          identifier={column?.id || column?.columnName}
                          value={formValues[column?.id || column?.columnName]}
                          error={errors[column?.id || column?.columnName]}
                          onChange={(value) =>
                            handleInputChange(
                              column?.id || column?.columnName,
                              value,
                            )
                          }
                          isDisable={isMyForm ? false : true}
                        />
                      </Col>
                    );
                  })}
                </Row>
              </CardLayout>
            ))
          ) : (
            <div className="text-center py-5 bg-white rounded-4 shadow-sm">
              <p className="text-muted mb-0">
                No fields available for this form.
              </p>
            </div>
          )}
          {(isTeamForm || isTeamSubmission) && (
            <CardLayout cardStyle="mb-4 border-0 shadow-sm p-0 bg-white rounded-4">
              <div className="d-flex align-items-center mb-4 pb-2 border-bottom">
                <div
                  className="section-icon-wrapper bg-warning-subtle p-2 rounded-3 me-3 d-flex align-items-center justify-content-center"
                  style={{ width: '36px', height: '36px' }}
                >
                  <i className="bi bi-star-fill text-warning fs-3" />
                </div>
                <h4 className="fw-bold mb-0 text-dark">
                  Manager's Review & Performance Rating
                </h4>
              </div>

              <div className="mb-5">
                <Label
                  id="rating"
                  labelClassname="fw-semibold d-block"
                  required
                >
                  Performance Rating
                </Label>
                <div className="d-flex align-items-center gap-3 mt-2">
                  <StarRating rating={managerRating} interactive={isTeamForm} />
                  <span className="fs-4 fw-bold text-primary">
                    {managerRating?.toFixed(1)}
                  </span>
                  <input
                    type="range"
                    className="form-range flex-grow-1 ms-3"
                    min="0"
                    max="5"
                    step="0.5"
                    disabled={!isTeamForm}
                    value={managerRating}
                    onChange={(e) =>
                      setManagerRating(parseFloat(e.target.value))
                    }
                    style={{ maxWidth: '200px' }}
                  />
                </div>
                <p className="text-muted small">
                  Select a rating from 0 to 5 (increments of 0.5)
                </p>
              </div>

              <div className="mb-4">
                <Label
                  id="remark"
                  required
                  labelClassname="form-label fw-semibold d-block"
                >
                  Manager's Remarks / Feedback
                </Label>
                <textarea
                  className="form-control mt-2 bg-light p-3 rounded-3"
                  rows={4}
                  placeholder="Provide detailed feedback on the employee's performance, strengths, and areas for improvement..."
                  value={managerRemark}
                  disabled={!isTeamForm}
                  onChange={(e) => setManagerRemark(e.target.value)}
                  style={{ resize: 'none' }}
                />
              </div>

              {isTeamForm && (
                <div className="text-end">
                  <button
                    type="button"
                    className="btn btn-primary fw-semibold rounded-3 shadow-sm"
                    onClick={submitManagerReview}
                  // disabled={isSubmittingReview}
                  >
                    {isSubmittingReview ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-save me-2 fs-4" />
                        Submit Review
                      </>
                    )}
                  </button>
                </div>
              )}
            </CardLayout>
          )}
          {isMyForm && (
            <div className="mb-4">
              <Label
                id="managerId"
                required
                labelClassname="form-label fw-semibold d-block"
              >
                Select Employee / Manager
              </Label>
              <SearchSelectBox
                isAsync={false}
                isUsingFilter={false}
                title="Select Employee"
                value={managerId}
                options={employeeOptions}
                onChange={(option) => setManagerId(option?.value || '')}
              />
            </div>
          )}
          {isMyForm && location?.state?.isSubmitted && (
            <div className="text-center mt-5 mb-4 p-4 bg-white rounded-4 shadow-sm">
              <p className="text-muted small mb-4">
                Review your answers before submitting. Your manager will receive
                a copy for the appraisal discussion.
              </p>
              <button
                type="button"
                onClick={handleSubmit}
                className="btn btn-primary  fw-semibold rounded-3 shadow-sm d-flex align-items-center mx-auto"
              >
                <i className="bi bi-check2-circle me-2 fs-4" />
                Submit appraisal
              </button>
            </div>
          )}
        </Container>
      </div>

      <style>{`
        .letter-spacing-1 { letter-spacing: 1px; }
        .bg-primary-subtle { background-color: rgba(13, 110, 253, 0.1); }
        .bg-warning-subtle { background-color: rgba(255, 193, 7, 0.1); }
        .lh-base { line-height: 1.6; }
        .form-view-container .card-layout {
          transition: transform 0.2s ease-in-out;
        }
        .bi-calendar3, .bi-clock, .bi-shield-lock { color: #6c757d; }
      `}</style>
    </Layout>
  );
};

export default FormView;
